import { RotateCw } from "lucide-react";
import { useState, useCallback } from "react";

/**
 * PullToRefresh component - provides pull-to-refresh functionality
 * @param {Object} props
 * @param {React.RefObject} props.containerRef - Reference to the scrollable container
 * @param {Function} props.onRefresh - Callback function to execute when refresh is triggered
 * @param {React.ReactNode} props.children - Child components to render
 */
const PullToRefresh = ({ containerRef, onRefresh, children }) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullStartY, setPullStartY] = useState(0);
  const [pullDistance, setPullDistance] = useState(0);
  const [lastTouchY, setLastTouchY] = useState(0);

  const getScrollTop = useCallback(() => {
    // Check document scrolling first
    const docScrollTop = document.documentElement.scrollTop;
    const bodyScrollTop = document.body.scrollTop;
    const windowScrollY = window.scrollY || window.pageYOffset || 0;

    // If any of these have a value, we're scrolled down in the main document
    if (docScrollTop > 0 || bodyScrollTop > 0 || windowScrollY > 0) {
      return docScrollTop || bodyScrollTop || windowScrollY;
    }

    // Check container ref if provided
    if (containerRef.current) {
      const containerScrollTop = containerRef.current.scrollTop;
      if (containerScrollTop > 0) {
        return containerScrollTop;
      }

      // Check if container has a scrollable parent
      let parent = containerRef.current.parentElement;
      while (parent) {
        const style = window.getComputedStyle(parent);
        const isScrollable =
          style.overflow === "auto" ||
          style.overflow === "scroll" ||
          style.overflowY === "auto" ||
          style.overflowY === "scroll";
        if (isScrollable && parent.scrollTop > 0) {
          return parent.scrollTop;
        }
        parent = parent.parentElement;
      }
    }

    return 0;
  }, [containerRef]);

  const handleTouchStart = useCallback(
    (e) => {
      const scrollTop = getScrollTop();
      const touchY = e.touches[0].clientY;

      if (scrollTop === 0) {
        setPullStartY(touchY);
        setLastTouchY(touchY);
      }
    },
    [getScrollTop]
  );

  const handleTouchMove = useCallback(
    (e) => {
      const scrollTop = getScrollTop();
      const touchY = e.touches[0].clientY;
      const delta = touchY - lastTouchY;

      // Only allow pull-to-refresh when at the top of the page and pulling down
      if (pullStartY > 0 && scrollTop === 0 && !isRefreshing) {
        // Only increase pull distance if moving down (positive delta)
        if (delta > 0 || pullDistance > 0) {
          setPullDistance((prev) => {
            const newDistance = Math.max(0, Math.min(prev + delta, 150));
            return newDistance;
          });

          setLastTouchY(touchY);

          // Prevent default scrolling when pulling to refresh
          if (pullDistance > 5) {
            e.preventDefault();
          }
        }
      } else {
        // Reset if we're not at the top anymore
        if (scrollTop > 0 && pullDistance > 0) {
          setPullDistance(0);
          setPullStartY(0);
          setLastTouchY(0);
        }
      }
    },
    [pullStartY, lastTouchY, isRefreshing, pullDistance, getScrollTop]
  );

  const handleTouchEnd = useCallback(async () => {
    if (pullDistance > 80 && !isRefreshing) {
      setIsRefreshing(true);
      setPullDistance(0);
      try {
        await onRefresh();
      } catch (error) {
        console.error("Error refreshing:", error);
      } finally {
        setIsRefreshing(false);
      }
    } else {
      setPullDistance(0);
    }
    setPullStartY(0);
    setLastTouchY(0);
  }, [pullDistance, isRefreshing, onRefresh]);

  const loaderOpacity = Math.min(pullDistance / 80, 1);
  const loaderTranslateY = Math.min(pullDistance * 0.5, 60);
  const showLoader = isRefreshing || pullDistance > 0;

  return (
    <>
      {/* Pull-to-refresh loader */}
      {showLoader && (
        <div
          className="fixed top-16 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-150 ease-out"
          style={{
            opacity: isRefreshing ? 1 : loaderOpacity,
            transform: `translate(-50%, ${
              isRefreshing ? "0px" : `-${60 - loaderTranslateY}px`
            })`,
          }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-full p-3 shadow-lg">
            <RotateCw
              className={`w-6 h-6 text-primary-100 transition-transform duration-150 ${
                isRefreshing ? "animate-spin" : ""
              }`}
              style={{
                transform: !isRefreshing
                  ? `rotate(${pullDistance * 2}deg)`
                  : undefined,
              }}
            />
          </div>
        </div>
      )}

      {/* Main content */}
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className="h-full w-screen"
      >
        {children}
      </div>
    </>
  );
};

export default PullToRefresh;
